import { useEffect, useState } from 'react';

interface UserFeedback {
  id: string;
  feedback_type: string;
  message: string;
  user_name: string | null;
  user_email: string | null;
  page_url: string | null;
  user_agent: string | null;
  screen_width: number | null;
  screen_height: number | null;
  status: string;
  team_notes: string | null;
  created_at: string;
  updated_at: string;
}

type StatusFilter = 'all' | 'new' | 'in_progress' | 'resolved' | 'closed';
type TypeFilter = 'all' | 'bug' | 'suggestion' | 'question' | 'general';

export const UserFeedbackList: React.FC = () => {
  const [feedback, setFeedback] = useState<UserFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<UserFeedback | null>(null);
  const [teamNotes, setTeamNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [total, setTotal] = useState(0);

  const apiUrl = import.meta.env.VITE_API_URL || '';

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const response = await fetch(`${apiUrl}/api/feedback?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch feedback');

      const data = await response.json();
      setFeedback(data.feedback || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, [statusFilter]);

  const handleStatusChange = async (feedbackId: string, newStatus: string) => {
    try {
      setSaving(true);
      const response = await fetch(`${apiUrl}/api/feedback/${feedbackId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update status');

      await fetchFeedback();
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedFeedback) return;

    try {
      setSaving(true);
      const response = await fetch(`${apiUrl}/api/feedback/${selectedFeedback.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_notes: teamNotes }),
      });

      if (!response.ok) throw new Error('Failed to save notes');

      await fetchFeedback();
      setSelectedFeedback({ ...selectedFeedback, team_notes: teamNotes });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (feedbackId: string) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return;

    try {
      const response = await fetch(`${apiUrl}/api/feedback/${feedbackId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete feedback');

      await fetchFeedback();
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete feedback');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bug': return '🐛';
      case 'suggestion': return '💡';
      case 'question': return '❓';
      default: return '💬';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-zinc-100 text-zinc-800';
      default: return 'bg-zinc-100 text-zinc-800';
    }
  };

  const filteredFeedback = feedback.filter((f) => {
    if (typeFilter !== 'all' && f.feedback_type !== typeFilter) return false;
    return true;
  });

  if (loading && feedback.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-zinc-900">User Feedback</h2>
          <p className="text-sm text-zinc-600">
            {total} total feedback items
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchFeedback}
            className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-all"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-sm font-medium text-zinc-700">
            Status:
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white cursor-pointer"
          >
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="type-filter" className="text-sm font-medium text-zinc-700">
            Type:
          </label>
          <select
            id="type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white cursor-pointer"
          >
            <option value="all">All Types</option>
            <option value="bug">Bug Reports</option>
            <option value="suggestion">Suggestions</option>
            <option value="question">Questions</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      {/* Feedback Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">Message</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {filteredFeedback.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-zinc-500">
                    No feedback items found
                  </td>
                </tr>
              ) : (
                filteredFeedback.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => {
                      setSelectedFeedback(item);
                      setTeamNotes(item.team_notes || '');
                    }}
                    className={`cursor-pointer transition-colors ${
                      selectedFeedback?.id === item.id
                        ? 'bg-zinc-100'
                        : 'hover:bg-zinc-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getTypeIcon(item.feedback_type)}</span>
                        <span className="text-sm font-medium text-zinc-900 capitalize">
                          {item.feedback_type}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <p className="text-sm text-zinc-600 truncate">
                        {item.message}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="text-zinc-900">{item.user_name || 'Anonymous'}</div>
                        {item.user_email && (
                          <div className="text-zinc-500 text-xs">{item.user_email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-500 whitespace-nowrap">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFeedback(item);
                            setTeamNotes(item.team_notes || '');
                          }}
                          className="text-zinc-600 hover:text-zinc-900 transition-colors"
                          title="View details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-zinc-900/50 transition-opacity"
              onClick={() => setSelectedFeedback(null)}
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getTypeIcon(selectedFeedback.feedback_type)}</span>
                    <div>
                      <h3 className="font-semibold text-zinc-900 capitalize">
                        {selectedFeedback.feedback_type}
                      </h3>
                      <p className="text-sm text-zinc-500">
                        {formatDate(selectedFeedback.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedFeedback(null)}
                    className="text-zinc-400 hover:text-zinc-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Status */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Status</label>
                  <select
                    value={selectedFeedback.status}
                    onChange={(e) => handleStatusChange(selectedFeedback.id, e.target.value)}
                    disabled={saving}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white"
                  >
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                {/* User Info */}
                <div className="mb-4 p-3 bg-zinc-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-zinc-500">Name:</span>
                      <span className="ml-2 text-zinc-900">{selectedFeedback.user_name || 'Anonymous'}</span>
                    </div>
                    <div>
                      <span className="text-zinc-500">Email:</span>
                      <span className="ml-2 text-zinc-900">
                        {selectedFeedback.user_email ? (
                          <a href={`mailto:${selectedFeedback.user_email}`} className="text-blue-600 hover:underline">
                            {selectedFeedback.user_email}
                          </a>
                        ) : (
                          'Not provided'
                        )}
                      </span>
                    </div>
                    {selectedFeedback.screen_width && selectedFeedback.screen_height && (
                      <div className="col-span-2">
                        <span className="text-zinc-500">Screen:</span>
                        <span className="ml-2 text-zinc-900">
                          {selectedFeedback.screen_width} x {selectedFeedback.screen_height}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Message</label>
                  <div className="p-3 bg-zinc-50 rounded-lg text-sm text-zinc-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selectedFeedback.message}
                  </div>
                </div>

                {/* Page URL */}
                {selectedFeedback.page_url && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Page URL</label>
                    <a
                      href={selectedFeedback.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline break-all"
                    >
                      {selectedFeedback.page_url}
                    </a>
                  </div>
                )}

                {/* Team Notes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Team Notes</label>
                  <textarea
                    value={teamNotes}
                    onChange={(e) => setTeamNotes(e.target.value)}
                    placeholder="Add internal notes about this feedback..."
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 min-h-[100px]"
                  />
                  <button
                    type="button"
                    onClick={handleSaveNotes}
                    disabled={saving || teamNotes === (selectedFeedback.team_notes || '')}
                    className="mt-2 px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-zinc-200">
                  <button
                    type="button"
                    onClick={() => handleDelete(selectedFeedback.id)}
                    className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-all"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedFeedback(null)}
                    className="ml-auto px-4 py-2 text-sm font-medium text-zinc-700 border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFeedbackList;
